
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"

import Guid from "@/scripts/guid"

import User from "@/types/user"
import { CaseDetails, Team, dataTypeByFieldType, DataType, Field } from "@/types"
import { caseColumns, CaseColumn, usersPlusDynamic } from "@/types/caseColumns"
import { InsightSearch, Operator, InsightCalculation, operatorLabels, operatorsByFieldType } from "@/types/insights"
import { dateRangeLabels } from "@/types/insights/dateRange"
import { DateClauseType } from "@/types/insights/dateClauseType"
import { DynamicValue } from "@/types/dynamic"

import vSelect from "vue-select"
// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import DataSourceSelect from "@/components/Fields/DataSourceSelect.vue"

import DateTime from "@/components/Fields/DateTime.vue"
import Numeric from "@/components/Fields/Numeric.vue"
import MapSearchBounds from "./MapSearchBounds.vue"

import api from "@/services/api"
import { Clause, SearchClauseParameters, DayOfWeek } from "@/types/view"
import { cloneDeep, isNull } from "lodash"
import MonthPicker from "@/components/Controls/MonthPicker.vue"

import moment from "moment"

const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		datetime: DateTime,
		numeric: Numeric,
		"map-search": MapSearchBounds,
		"dataSourceSelect": DataSourceSelect,
		"month-picker": MonthPicker
	}
})
export default class DateTimeParameterValue extends Vue {
	@Getter getDarkMode!: boolean;

	@Prop({ default: null })
	field!: CaseColumn;

	@Prop({ default: null })
	value!: DynamicValue | null;

	@Prop({ default: null })
	operator!: Operator | null;

	@Prop({ default: true })
	allowDynamic!: boolean

	@Prop()
	selectedSearchClauseParameters!: SearchClauseParameters

	@Prop({ default: () => Guid.newGuid() })
	clauseIndex!: number | string;

	private selectedDayOfWeek: string | null = null;
	private weekNumber: number| null = null;
	private startDate: Date | null = null;
	private endDate: Date | null = null;
	private startMonthRange: Date | null = null;
	private endMonthRange: Date | null = null;
	private selectedDateClauseType: DateClauseType | null = null;

	private dateRangeLabels = dateRangeLabels
	private DateClauseType = DateClauseType

	private selectedItem = null;
	DayOfWeek = DayOfWeek

	private get dateValue (): string | null {
		return this.value ? this.value.value : null
	}

	private get dynamicValue (): boolean {
		return this.value ? this.value.dynamic : false
	}

	private get daysOfWeekNames (): string[] {
		return Object.values(DayOfWeek).filter(value => typeof value === "string") as string[]
	}

	private get monthRangeStartDate (): Date {
		return this.selectedSearchClauseParameters!.startDate!
	}

	private get monthRangeEndDate (): Date {
		var dateRange = this.selectedSearchClauseParameters!.endDate ? this.selectedSearchClauseParameters.endDate : moment().endOf("year").toDate()
		return this.selectedSearchClauseParameters!.endDate!
	}

	private get hasWeekStart (): boolean {
		return dateRangeLabels.findAndGet(l => l.id === this.dateValue, l => l.showWeekStart, false)
	}

	private get dateClauseType (): DateClauseType | null {
		return this.selectedDateClauseType
	}

	private get shouldBeDynamic (): boolean {
		return this.selectedDateClauseType !== DateClauseType.ExactDate || this.operator === Operator.DateRange
	}

	private set dateClauseType (value: DateClauseType | null) {
		this.selectedDateClauseType = value

		var selectedDateRange = this.selectedDateRange(value!)
		if (selectedDateRange) {
			this.valueChanged(selectedDateRange)
		} else {
			this.valueChanged(null)
		}
	}

	private selectedDateRange (value: DateClauseType): string {
		var selectedDateRange = ""

		switch (this.selectedDateClauseType) {
			case DateClauseType.CurrentWeek:
				selectedDateRange = "CurrentWeek"
				break
			case DateClauseType.CurrentMonth:
				selectedDateRange = "CurrentMonth"
				break
			case DateClauseType.YearToDate:
				selectedDateRange = "YearToDate"
				break
			case DateClauseType.WeekNumber:
				selectedDateRange = "WeekNumber"
				break
			case DateClauseType.MonthRange:
				selectedDateRange = "MonthRange"
				break
			case DateClauseType.DateRange:
				selectedDateRange = "DateRange"
				break
		}
		return selectedDateRange
	}

	mounted () {
		this.selectedDateClauseType = this.getDateClauseType()
		this.assignSearchClauseParamaters(this.selectedSearchClauseParameters)
	}

	@Watch("selectedSearchClauseParameters")
	ValuePropChanged (newParamaters: SearchClauseParameters) {
		this.assignSearchClauseParamaters(newParamaters)
	}

	assignSearchClauseParamaters (newParamaters: SearchClauseParameters) {
		if (!newParamaters) {
			return
		}

		this.selectedDayOfWeek = this.DayOfWeek[newParamaters.startDayOfWeek]

		this.weekNumber = newParamaters.weekNumber ? newParamaters.weekNumber : null
		this.startDate = newParamaters.startDate ? newParamaters.startDate : null
		this.endDate = newParamaters.endDate ? newParamaters.endDate : null

		this.startMonthRange = newParamaters.startMonthRange ? newParamaters.startMonthRange : null
		this.endMonthRange = newParamaters.endMonthRange ? newParamaters.endMonthRange : null
		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	getDateClauseType (): DateClauseType {
		if (this.value === null) {
			return DateClauseType.RelativeCurrentDate
		}

		if (this.dynamicValue) {
			switch (this.value.value) {
				case "CurrentWeek":
					return DateClauseType.CurrentWeek
				case "CurrentMonth":
					return DateClauseType.CurrentMonth
				case "YearToDate":
					return DateClauseType.YearToDate
				case "WeekNumber":
					return DateClauseType.WeekNumber
				case "DateRange":
					return DateClauseType.DateRange
				case "MonthRange":
					return DateClauseType.MonthRange
			}

			if (this.dateValue && dateRangeLabels.map(c => c.id).includes(this.dateValue)) {
				return DateClauseType.RelativeDateRange
			} else {
				return DateClauseType.RelativeCurrentDate
			}
		}
		return DateClauseType.ExactDate
	}

	startDayOfWeekChanged (newStartDayOfWeekLabel: string) {
		this.$set(this.selectedSearchClauseParameters, "startDayOfWeek", (DayOfWeek as any)[newStartDayOfWeekLabel])
		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
		this.selectedDayOfWeek = newStartDayOfWeekLabel
	}

	startMonthChanged (newStartMonth: Date) {
		var startMonth = moment(newStartMonth).utc().toDate()
		this.$set(this.selectedSearchClauseParameters, "startMonthRange", startMonth)
		this.startMonthRange = startMonth

		// Set endMonth to the start date
		if (this.endMonthRange! < startMonth) {
			var endMonth = moment(startMonth).add("month", 1).subtract("millisecond", 1).toDate()
			this.$set(this.selectedSearchClauseParameters, "endMonthRange", endMonth)
			this.endMonthRange = endMonth
		}

		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	endMonthChanged (newEndMonth: Date) {
		var endMonth = moment(newEndMonth).subtract("millisecond", 1).utc().toDate()
		this.$set(this.selectedSearchClauseParameters, "endMonthRange", endMonth)
		this.endMonthRange = endMonth

		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	valueChanged (newValue: any) {
		var updatedValue = cloneDeep(this.value) || {
			value: null,
			dynamic: false
		}

		if (updatedValue.value !== newValue) {
			updatedValue.dynamic = this.shouldBeDynamic
			updatedValue.value = newValue

			this.$emit("input", updatedValue)
		}
	}

	dateValueChanged (newValue: Date) {
		this.valueChanged(new Date(newValue.getFullYear(),
			newValue.getMonth(),
			newValue.getDate()
		).toISOString())
	}

	dateClauseChanged (newValue: DateClauseType) {
		if (newValue === DateClauseType.ExactDate) {
			this.dynamicValueChanged(false)
		} else {
			this.dynamicValueChanged(true)
		}
	}

	numberValueChanged (newValue: number) {
		let updatedValue = cloneDeep(this.value)

		if (updatedValue === null) {
			updatedValue = {
				value: null,
				dynamic: this.selectedDateClauseType !== DateClauseType.ExactDate
			}
		}

		if (updatedValue.value !== newValue) {
			updatedValue.value = newValue
			this.$emit("input", updatedValue)
		}
	}

	dynamicValueChanged (newValue: boolean) {
		var updatedValue = cloneDeep(this.value) || { value: null, dynamic: false }

		if (updatedValue.dynamic !== newValue) {
			updatedValue.dynamic = newValue
			this.$emit("input", updatedValue)
		}
	}

	dateRangeDateValueChanged (newValue: Date, dateRangeType: string) {
		if (dateRangeType === "startDate") {
			newValue.setHours(0, 0, 0, 0)
			this.$set(this.selectedSearchClauseParameters, "startDate", newValue)
			this.startDate = newValue
		} else if (dateRangeType === "endDate") {
			newValue.setHours(23, 59, 59, 999)
			this.$set(this.selectedSearchClauseParameters, "endDate", newValue)
			this.endDate = newValue
		}

		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	weekNumberChanged (newWeekNumber: number) {
		// validation
		if (newWeekNumber > 52 || newWeekNumber < 1) {
			this.$set(this.selectedSearchClauseParameters, "weekNumber", null)
			this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
		} else {
			this.$set(this.selectedSearchClauseParameters, "weekNumber", newWeekNumber)
			this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
		}
	}
}

